import { useEffect, useState, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
  PieChart, Pie,
} from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Phone, ArrowSquareOut, MagnifyingGlass, CaretRight, DownloadSimple } from '@phosphor-icons/react';
import { cxGet, fmtMoney, fmtNum, StatCard, Panel, CoverageBanner, useCxEntity, authHeaders } from './cxShared';
import { Customer360 } from './CxCustomers';
import axios from 'axios';
import { API_URL } from '../../utils/api';
import { toast } from 'sonner';

const PERF_COLORS = { Excellent: '#0A2540', Good: '#4A5A6A', Normal: '#8A97A3', Dormant: '#C4C9CE' };
const TIER_META = [
  { k: 'Gold', color: '#C9A35B', note: '≥100% of ARU target · active all 3 months' },
  { k: 'Silver', color: '#8A97A3', note: '50–99% of ARU target · active ≥2 of 3 months' },
  { k: 'Bronze', color: '#C7A98B', note: '<50% of target · new / low history' },
];
const SPECTRUM_META = [
  { k: 'active', label: 'Active', range: '0–7d', color: '#0A2540' },
  { k: 'cooling', label: 'Cooling', range: '8–14d', color: '#4A5A6A' },
  { k: 'at_risk', label: 'At Risk', range: '15–29d', color: '#8A97A3' },
  { k: 'lost', label: 'Lost', range: '30d+', color: '#C4C9CE' },
  { k: 'never', label: 'Never refilled', range: '—', color: '#E4DED2' },
];

const rateBand = (rate) => {
  const r = Number(rate) || 0;
  if (r >= 75) return { label: 'Excellent', color: '#2F6D4B' };
  if (r >= 65) return { label: 'Strong / Healthy', color: '#0A2540' };
  if (r >= 55) return { label: 'Acceptable', color: '#8A6D2B' };
  if (r >= 45) return { label: 'Warning', color: '#B4794A' };
  return { label: 'Critical', color: '#B4453A' };
};

const penBand = (rate) => {
  const r = Number(rate) || 0;
  if (r >= 90) return { label: 'Excellent', color: '#2F6D4B' };
  if (r >= 80) return { label: 'Strong / Target', color: '#0A2540' };
  if (r >= 70) return { label: 'Acceptable', color: '#8A6D2B' };
  if (r >= 60) return { label: 'Warning', color: '#B4794A' };
  return { label: 'Critical', color: '#B4453A' };
};

const monthLabel = (mk) => {
  if (!mk) return '';
  const [y, m] = mk.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString('en', { month: 'short' });
};

const monthLabelFull = (mk) => {
  if (!mk) return '';
  const [y, m] = mk.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString('en', { month: 'short', year: 'numeric' });
};

function AruDrillDialog({ month, entityId, onClose }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const es = entityId && entityId !== 'all' ? `&entity_id=${entityId}` : '';
        setD(await cxGet(`/cx/aru-breakdown?month=${month}${es}`));
      } catch (e) { toast.error('Failed to load breakdown'); }
      finally { setLoading(false); }
    })();
  }, [month, entityId]);
  const cur = d?.currency || '';
  const Section = ({ title, rows, testid }) => (
    <div data-testid={testid}>
      <p className="text-[10px] tracking-[0.15em] uppercase text-[#78716C] font-semibold mb-1.5" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{title}</p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[10px] uppercase text-[#A8A29E] border-b border-[#EDE6D8]">
            <th className="py-1.5 font-semibold">Name</th>
            <th className="py-1.5 font-semibold text-right">Active</th>
            <th className="py-1.5 font-semibold text-right">Revenue</th>
            <th className="py-1.5 font-semibold text-right">ARU</th>
            <th className="py-1.5 font-semibold text-right">vs Target</th>
          </tr>
        </thead>
        <tbody>
          {(rows || []).map((r) => (
            <tr key={r.name} className="border-b border-[#F4F0E8]">
              <td className="py-2 text-[#0A2540] font-medium">{r.name}</td>
              <td className="py-2 text-right text-[#44403C]">{fmtNum(r.active)}</td>
              <td className="py-2 text-right text-[#44403C]">{fmtMoney(r.revenue, cur)}</td>
              <td className="py-2 text-right font-semibold text-[#0A2540]">{fmtMoney(r.aru, cur)}</td>
              <td className="py-2 text-right">
                {r.variance_pct == null ? <span className="text-[#A8A29E]">—</span> :
                  <span className={r.variance_pct >= 0 ? 'text-[#3F7D5B] font-semibold' : 'text-[#B4794A] font-semibold'}>{r.variance_pct >= 0 ? '+' : ''}{r.variance_pct}%</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  const t = d?.total;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cx-aru-drill">
        <DialogHeader><DialogTitle className="text-[#0A2540]">ARU breakdown · {monthLabelFull(month)}</DialogTitle></DialogHeader>
        {loading ? <p className="text-center py-10 text-[#78716C]">Loading…</p> : !d ? null : (
          <div className="space-y-6">
            <div className="flex flex-wrap gap-6 bg-[#FCFAF6] rounded-xl p-4">
              <div><p className="text-[10px] uppercase text-[#78716C]">Active</p><p className="text-lg font-bold text-[#0A2540]">{fmtNum(t.active)}</p></div>
              <div><p className="text-[10px] uppercase text-[#78716C]">ARU</p><p className="text-lg font-bold text-[#0A2540]">{fmtMoney(t.aru, cur)}</p></div>
              <div><p className="text-[10px] uppercase text-[#78716C]">Target</p><p className="text-lg font-semibold text-[#57534E]">{fmtMoney(t.target, cur)}</p></div>
              {t.variance_pct != null && <div><p className="text-[10px] uppercase text-[#78716C]">Variance</p><p className={`text-lg font-bold ${t.variance_pct >= 0 ? 'text-[#3F7D5B]' : 'text-[#B4794A]'}`}>{t.variance_pct >= 0 ? '+' : ''}{t.variance_pct}%</p></div>}
            </div>
            <Section title="By Customer Category" rows={d.by_category} testid="drill-by-category" />
            <Section title="By Campus" rows={d.by_campus} testid="drill-by-campus" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function WorklistDialog({ bucket, label, entityId, onOpen, onClose }) {
  const [d, setD] = useState({ rows: [], total: 0, page: 1, total_pages: 1 });
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [dl, setDl] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const es = entityId && entityId !== 'all' ? `&entity_id=${entityId}` : '';
      const qq = q ? `&q=${encodeURIComponent(q)}` : '';
      setD(await cxGet(`/cx/worklist?bucket=${bucket}&page=${page}&page_size=25${es}${qq}`));
    } catch (e) { toast.error('Failed to load worklist'); }
    finally { setLoading(false); }
  }, [bucket, entityId, page, q]);
  useEffect(() => { load(); }, [load]);
  const cur = d.rows?.[0]?.currency || '';
  const downloadXlsx = async () => {
    setDl(true);
    try {
      const es = entityId && entityId !== 'all' ? `&entity_id=${entityId}` : '';
      const qq = q ? `&q=${encodeURIComponent(q)}` : '';
      const res = await axios.get(`${API_URL}/api/cx/worklist/export?bucket=${bucket}${es}${qq}`, { headers: authHeaders(), responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `winback-${bucket}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(link.href);
      toast.success('Win-back list downloaded');
    } catch (e) { toast.error('Download failed'); }
    finally { setDl(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="cx-worklist">
        <DialogHeader><DialogTitle className="text-[#0A2540]">Win-back worklist · {label}</DialogTitle></DialogHeader>
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="relative flex-1 max-w-xs">
            <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A8A29E]" />
            <Input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search name or mobile" className="pl-9 border-[#EDE6D8] rounded-lg h-9" data-testid="worklist-search" />
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="text-xs text-[#78716C]">{fmtNum(d.total)} customers</span>
            <Button variant="outline" size="sm" onClick={downloadXlsx} disabled={dl || d.total === 0} className="border-[#EDE6D8] rounded-lg h-9 text-[#0A2540]" data-testid="worklist-download">
              <DownloadSimple size={14} className="mr-1.5" />{dl ? 'Preparing…' : 'Download'}
            </Button>
          </div>
        </div>
        {loading ? <p className="text-center py-10 text-[#78716C]">Loading…</p> : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase text-[#A8A29E] border-b border-[#EDE6D8]">
                    <th className="py-1.5 font-semibold">Customer</th>
                    <th className="py-1.5 font-semibold">Last refill</th>
                    <th className="py-1.5 font-semibold text-right">Tier</th>
                    <th className="py-1.5 font-semibold text-right">Wallet</th>
                    <th className="py-1.5 font-semibold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {d.rows.map((r) => (
                    <tr key={r.sub_id} className="border-b border-[#F4F0E8]" data-testid={`worklist-row-${r.sub_id}`}>
                      <td className="py-2.5">
                        <p className="text-[#0A2540] font-medium">{r.name}</p>
                        <p className="text-xs text-[#78716C]">{r.mobile || '—'}</p>
                      </td>
                      <td className="py-2.5 text-[#44403C]">{r.last_refill ? r.last_refill.slice(0, 10) : '—'}{r.days_since != null && <span className="text-xs text-[#B4794A] ml-1">{r.days_since}d</span>}</td>
                      <td className="py-2.5 text-right">{r.tier}</td>
                      <td className="py-2.5 text-right text-[#44403C]">{fmtMoney(r.wallet_balance, r.currency || cur)}</td>
                      <td className="py-2.5">
                        <div className="flex items-center justify-end gap-1.5">
                          {r.mobile && (
                            <a href={`tel:${r.mobile}`} data-testid={`worklist-call-${r.sub_id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0A2540] border border-[#EDE6D8] rounded-lg px-2.5 h-8 hover:bg-[#FCFAF6]">
                              <Phone size={13} /> Call
                            </a>
                          )}
                          <button onClick={() => onOpen(r.sub_id)} data-testid={`worklist-open-${r.sub_id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-[#0A2540] rounded-lg px-2.5 h-8 hover:bg-[#0A2540]/90">
                            <ArrowSquareOut size={13} /> Open
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {d.rows.length === 0 && <tr><td colSpan={5} className="py-8 text-center text-[#78716C]">No customers in this bucket.</td></tr>}
                </tbody>
              </table>
            </div>
            {d.total_pages > 1 && (
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-[#78716C]">Page {d.page} of {d.total_pages}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="border-[#EDE6D8] rounded-lg h-8" data-testid="worklist-prev">Prev</Button>
                  <Button variant="outline" size="sm" disabled={page >= d.total_pages} onClick={() => setPage((p) => p + 1)} className="border-[#EDE6D8] rounded-lg h-8" data-testid="worklist-next">Next</Button>
                </div>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Bars = ({ rows, total, colorFor, testid }) => (
  <div className="space-y-3" data-testid={testid}>
    {rows.map(({ key, label, value, note }) => {
      const pct = total ? Math.round((value / total) * 100) : 0;
      return (
        <div key={key}>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-[#44403C]">{label}{note && <span className="text-[#A8A29E] text-xs ml-2">{note}</span>}</span>
            <span className="font-semibold text-[#0A2540]">{fmtNum(value)} · {pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-[#F4F0E8] overflow-hidden">
            <div style={{ width: `${pct}%`, background: colorFor(key) }} className="h-full rounded-full" />
          </div>
        </div>
      );
    })}
  </div>
);

export default function CxOverview() {
  const { entityId } = useCxEntity();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drillMonth, setDrillMonth] = useState(null);
  const [worklist, setWorklist] = useState(null);
  const [openSub, setOpenSub] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = entityId && entityId !== 'all' ? `?entity_id=${entityId}` : '';
      setData(await cxGet(`/cx/overview${qs}`));
    } catch (e) { toast.error('Failed to load overview'); }
    finally { setLoading(false); }
  }, [entityId]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="text-center py-20 text-[#78716C]" data-testid="cx-overview-loading">Loading…</div>;
  if (!data) return null;

  const cur = data.currency || '';
  const aru = data.aru || {};
  const perf = data.performance_mix || {};
  const perfTotal = Object.values(perf).reduce((a, b) => a + b, 0) || 1;
  const tiersTotal = Object.values(data.tiers || {}).reduce((a, b) => a + b, 0) || 1;
  const spectrumTotal = Object.values(data.spectrum || {}).reduce((a, b) => a + b, 0) || 1;
  const varUp = (aru.variance_pct ?? 0) >= 0;
  const activeBand = rateBand(data.active_customer_rate);
  const activeDelta = Number(data.active_customer_rate || 0) - Number(data.target_active_rate || 0);
  const pen = data.penetration || {};
  const penBandv = penBand(pen.rate);
  const penDelta = Number(pen.rate || 0) - Number(pen.target || 0);
  const spectrumDonut = SPECTRUM_META
    .map((s) => ({ name: s.label, value: (data.spectrum || {})[s.k] || 0, color: s.color }))
    .filter((d) => d.value > 0);

  return (
    <div className="space-y-4" data-testid="cx-overview-page">
      <CoverageBanner asOf={data.metrics_as_of} cached={data.coverage?.cached} total={data.coverage?.total} onRefreshed={load} />

      {/* ── KPI row ─────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard testid="kpi-total-customers" label="Registered Customers" value={fmtNum(data.total_customers)} sub={`${fmtNum(data.new_signups)} new in ${data.new_signups_days}d`} />
        <StatCard testid="kpi-active" label="Active This Month" value={fmtNum(data.active_customers)} sub={`${data.active_customer_rate}% active rate`} />
        <StatCard testid="kpi-aru" label={`Monthly ARU${cur ? ` (${cur})` : ''}`} value={aru.ready ? fmtMoney(aru.actual, cur) : '—'}
          sub={aru.ready && aru.target ? `Target ${fmtMoney(aru.target, cur)}` : 'awaiting metrics'} />
        <StatCard testid="kpi-monthly-revenue" label="Monthly Refill Revenue" value={fmtMoney(data.monthly_refill_revenue, cur)}
          sub={data.monthly_refill_volume_l != null ? `${fmtNum(data.monthly_refill_volume_l)} L` : null} />
      </div>

      {/* ── ARU vs Target + trend ──────────────────────────── */}
      <Panel title="ARU vs Target" testid="cx-aru-panel"
        actions={aru.ready && aru.variance_pct != null && (
          <span data-testid="cx-aru-variance" className={`text-xs font-semibold px-2.5 py-1 rounded-full ${varUp ? 'bg-[#0A2540] text-white' : 'bg-[#E4DED2] text-[#57534E]'}`}>
            {varUp ? '▲' : '▼'} {Math.abs(aru.variance_pct)}% vs target
          </span>
        )}>
        {!aru.ready ? (
          <p className="text-sm text-[#78716C] py-8 text-center">ARU is computed from monthly refill activity — trigger a metrics refresh above to populate it.</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-4">
              <div>
                <p className="text-[10px] tracking-[0.15em] uppercase text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Actual · {monthLabel(aru.month)}</p>
                <p className="text-3xl font-bold text-[#0A2540]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{fmtMoney(aru.actual, cur)}</p>
              </div>
              <div>
                <p className="text-[10px] tracking-[0.15em] uppercase text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Target</p>
                <p className="text-xl font-semibold text-[#57534E]">{fmtMoney(aru.target, cur)}</p>
                {aru.single_entity && aru.volume_target_l != null && (
                  <p className="text-xs text-[#A8A29E] mt-1">{aru.volume_target_l} L × {fmtMoney(aru.price_per_litre, cur)}/L</p>
                )}
              </div>
            </div>
            <div className="lg:col-span-2" style={{ height: 200 }} data-testid="cx-aru-trend">
              <ResponsiveContainer>
                <BarChart data={(aru.trend || []).map((t) => ({ ...t, m: monthLabel(t.month) }))} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                  <XAxis dataKey="m" tick={{ fontSize: 11, fill: '#78716C' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#78716C' }} axisLine={false} tickLine={false} width={48} />
                  <Tooltip formatter={(v, n) => n === 'aru' ? [fmtMoney(v, cur), 'ARU'] : [v, n]} cursor={{ fill: '#FCFAF6' }} />
                  <Bar dataKey="aru" radius={[4, 4, 0, 0]} cursor="pointer" onClick={(pt) => pt && pt.month && setDrillMonth(pt.month)}>
                    {(aru.trend || []).map((t, i) => (
                      <Cell key={i} fill={aru.target && t.aru >= aru.target ? '#0A2540' : '#B8C0C7'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </Panel>

      {/* ── Active Customer Rate ───────────────────────────── */}
      <Panel title="Active Customer Rate" testid="cx-active-rate-panel"
        actions={<span className="text-xs text-[#78716C]">Ecowater target ≥ {data.target_active_rate}%</span>}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="flex items-end gap-2">
              <span className="text-5xl font-bold" style={{ color: activeBand.color, fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="active-rate-value">{data.active_customer_rate}%</span>
            </div>
            <span className="inline-block mt-2 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: `${activeBand.color}18`, color: activeBand.color }} data-testid="active-rate-band">{activeBand.label}</span>
            <p className="text-xs text-[#78716C] mt-3">{fmtNum(data.active_customers)} active of {fmtNum(data.total_customers)} registered</p>
            <p className="text-xs mt-1" data-testid="active-rate-delta">
              {activeDelta >= 0
                ? <span className="text-[#3F7D5B] font-semibold">{activeDelta.toFixed(1)} pts above target</span>
                : <span className="text-[#B4794A] font-semibold">{Math.abs(activeDelta).toFixed(1)} pts below target</span>}
              <span className="text-[#A8A29E]"> · target {data.target_active_rate}%</span>
            </p>
            <div className="relative mt-5 h-3 rounded-full bg-[#EDE6D8] overflow-hidden">
              <div className="absolute inset-y-0 left-0 rounded-full transition-all" style={{ width: `${Math.min(100, Number(data.active_customer_rate) || 0)}%`, background: activeBand.color }} />
            </div>
            <div className="relative h-4 mt-0.5">
              <div className="absolute top-0 w-px h-3 bg-[#1C1917]" style={{ left: `${Math.min(100, Number(data.target_active_rate) || 0)}%` }} />
              <span className="absolute top-2.5 text-[9px] text-[#78716C] -translate-x-1/2" style={{ left: `${Math.min(100, Number(data.target_active_rate) || 0)}%` }}>target</span>
            </div>
          </div>
          <div className="lg:col-span-2" data-testid="active-rate-by-campus">
            <p className="text-[10px] tracking-[0.15em] uppercase text-[#78716C] font-semibold mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>By campus</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase text-[#A8A29E] border-b border-[#EDE6D8]">
                    <th className="py-1.5 font-semibold">Campus</th>
                    <th className="py-1.5 font-semibold text-right">Registered</th>
                    <th className="py-1.5 font-semibold text-right">Active</th>
                    <th className="py-1.5 font-semibold text-right">Rate</th>
                    <th className="py-1.5 font-semibold text-right">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_campus || []).length === 0 ? (
                    <tr><td colSpan={5} className="py-6 text-center text-[#A8A29E]">No campus data.</td></tr>
                  ) : (data.by_campus || []).map((c) => {
                    const b = rateBand(c.active_rate);
                    return (
                      <tr key={c.campus} className="border-b border-[#F4F0E8]" data-testid={`active-campus-row`}>
                        <td className="py-2 text-[#0A2540] font-medium">{c.campus}</td>
                        <td className="py-2 text-right text-[#44403C]">{fmtNum(c.registered)}</td>
                        <td className="py-2 text-right text-[#44403C]">{fmtNum(c.active)}</td>
                        <td className="py-2 text-right font-semibold" style={{ color: b.color }}>{c.active_rate}%</td>
                        <td className="py-2 text-right"><span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${b.color}18`, color: b.color }}>{b.label}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </Panel>

      {/* ── Subscriber Penetration Rate ────────────────────── */}
      <Panel title="Subscriber Penetration Rate" testid="cx-penetration-panel"
        actions={<span className="text-xs text-[#78716C]">Ecowater target ≥ {pen.target}%</span>}>
        {!pen.configured ? (
          <div className="py-6 text-center" data-testid="penetration-unconfigured">
            <p className="text-sm text-[#78716C]">No serviceable market (SAM) configured yet.</p>
            <p className="text-xs text-[#A8A29E] mt-1">Set each campus's TAM, a SAM % and a penetration target under <span className="font-semibold text-[#0A2540]">Configurations → CX</span> to see penetration here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <div className="flex items-end gap-2">
                <span className="text-5xl font-bold" style={{ color: penBandv.color, fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="penetration-value">{pen.rate}%</span>
              </div>
              <span className="inline-block mt-2 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: `${penBandv.color}18`, color: penBandv.color }} data-testid="penetration-band">{penBandv.label}</span>
              <p className="text-xs text-[#78716C] mt-3">{fmtNum(pen.registered)} registered of {fmtNum(pen.sam ?? pen.addressable)} serviceable (SAM)</p>
              <p className="text-[11px] text-[#A8A29E] mt-0.5" data-testid="penetration-sam-basis">SAM = {fmtNum(pen.tam)} TAM × {pen.sam_percent}%</p>
              <p className="text-xs mt-1" data-testid="penetration-delta">
                {penDelta >= 0
                  ? <span className="text-[#3F7D5B] font-semibold">{penDelta.toFixed(1)} pts above target</span>
                  : <span className="text-[#B4794A] font-semibold">{Math.abs(penDelta).toFixed(1)} pts below target</span>}
                <span className="text-[#A8A29E]"> · target {pen.target}%</span>
              </p>
              <div className="relative mt-5 h-3 rounded-full bg-[#EDE6D8] overflow-hidden">
                <div className="absolute inset-y-0 left-0 rounded-full transition-all" style={{ width: `${Math.min(100, Number(pen.rate) || 0)}%`, background: penBandv.color }} />
              </div>
              <div className="relative h-4 mt-0.5">
                <div className="absolute top-0 w-px h-3 bg-[#1C1917]" style={{ left: `${Math.min(100, Number(pen.target) || 0)}%` }} />
                <span className="absolute top-2.5 text-[9px] text-[#78716C] -translate-x-1/2" style={{ left: `${Math.min(100, Number(pen.target) || 0)}%` }}>target</span>
              </div>
            </div>
            <div className="lg:col-span-2" data-testid="penetration-by-campus">
              <p className="text-[10px] tracking-[0.15em] uppercase text-[#78716C] font-semibold mb-2" style={{ fontFamily: 'JetBrains Mono, monospace' }}>By campus</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase text-[#A8A29E] border-b border-[#EDE6D8]">
                      <th className="py-1.5 font-semibold">Campus</th>
                      <th className="py-1.5 font-semibold text-right">Registered</th>
                      <th className="py-1.5 font-semibold text-right">TAM</th>
                      <th className="py-1.5 font-semibold text-right">SAM</th>
                      <th className="py-1.5 font-semibold text-right">Rate</th>
                      <th className="py-1.5 font-semibold text-right">Rating</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(pen.by_campus || []).length === 0 ? (
                      <tr><td colSpan={6} className="py-6 text-center text-[#A8A29E]">No campus data.</td></tr>
                    ) : (pen.by_campus || []).map((c) => {
                      const configured = c.penetration_rate != null;
                      const b = configured ? penBand(c.penetration_rate) : null;
                      return (
                        <tr key={c.campus} className="border-b border-[#F4F0E8]" data-testid="penetration-campus-row">
                          <td className="py-2 text-[#0A2540] font-medium">{c.campus}</td>
                          <td className="py-2 text-right text-[#44403C]">{fmtNum(c.registered)}</td>
                          <td className="py-2 text-right text-[#78716C]">{c.tam > 0 ? fmtNum(c.tam) : '—'}</td>
                          <td className="py-2 text-right text-[#44403C]">{c.sam > 0 ? fmtNum(c.sam) : '—'}</td>
                          <td className="py-2 text-right font-semibold" style={{ color: b ? b.color : '#A8A29E' }}>{configured ? `${c.penetration_rate}%` : '—'}</td>
                          <td className="py-2 text-right">
                            {b
                              ? <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: `${b.color}18`, color: b.color }}>{b.label}</span>
                              : <span className="text-[10px] text-[#A8A29E]">Not set</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </Panel>

      {/* ── Monthly ARU history ────────────────────────────── */}
      {aru.ready && (aru.trend || []).length > 0 && (
        <Panel title="Monthly ARU History" testid="cx-aru-history">
          <p className="text-xs text-[#A8A29E] mb-3">Click any month to see which campuses and categories drove ARU.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] tracking-[0.15em] uppercase text-[#78716C] border-b border-[#EDE6D8]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  <th className="py-2 font-semibold">Month</th>
                  <th className="py-2 font-semibold text-right">Active</th>
                  <th className="py-2 font-semibold text-right">Refill Revenue</th>
                  <th className="py-2 font-semibold text-right">ARU</th>
                  <th className="py-2 font-semibold text-right">vs Target</th>
                  <th className="py-2 w-6"></th>
                </tr>
              </thead>
              <tbody>
                {[...(aru.trend || [])].reverse().map((t) => {
                  const vpct = aru.target ? Math.round((t.aru - aru.target) / aru.target * 1000) / 10 : null;
                  return (
                    <tr key={t.month} onClick={() => setDrillMonth(t.month)} className="border-b border-[#F4F0E8] cursor-pointer hover:bg-[#FCFAF6]" data-testid={`aru-history-${t.month}`}>
                      <td className="py-2.5 text-[#0A2540] font-medium">{monthLabelFull(t.month)}</td>
                      <td className="py-2.5 text-right text-[#44403C]">{fmtNum(t.active)}</td>
                      <td className="py-2.5 text-right text-[#44403C]">{fmtMoney(t.revenue, cur)}</td>
                      <td className="py-2.5 text-right font-semibold text-[#0A2540]">{fmtMoney(t.aru, cur)}</td>
                      <td className="py-2.5 text-right">{vpct == null || !t.active ? <span className="text-[#A8A29E]">—</span> : <span className={vpct >= 0 ? 'text-[#3F7D5B] font-semibold' : 'text-[#B4794A] font-semibold'}>{vpct >= 0 ? '+' : ''}{vpct}%</span>}</td>
                      <td className="py-2.5 text-right text-[#C4C9CE]"><CaretRight size={14} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {/* ── Conversion funnel ──────────────────────────────── */}
      <Panel title="Conversion Funnel" testid="cx-funnel-panel">
        <p className="text-xs text-[#A8A29E] mb-3">"Topped Up Again" counts customers who recharged beyond the default first recharge given at registration.</p>
        <div className="space-y-3">
          {(data.funnel || []).map((f, i) => (
            <div key={f.stage} data-testid={`funnel-${i}`}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-[#44403C]">{f.stage}</span>
                <span className="font-semibold text-[#0A2540]">{fmtNum(f.count)} · {f.pct}%{i > 0 && f.drop ? <span className="text-[#B4794A] text-xs ml-2">−{fmtNum(f.drop)} drop-off</span> : null}</span>
              </div>
              <div className="h-2.5 rounded-full bg-[#F4F0E8] overflow-hidden">
                <div style={{ width: `${f.pct}%`, background: '#0A2540' }} className="h-full rounded-full" />
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {/* ── Performance levels + Tiers ─────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Performance Levels" testid="cx-perf-mix">
          <Bars
            testid="cx-perf-bars"
            total={perfTotal}
            colorFor={(k) => PERF_COLORS[k]}
            rows={['Excellent', 'Good', 'Normal', 'Dormant'].map((k) => ({ key: k, label: k, value: perf[k] || 0 }))}
          />
        </Panel>
        <Panel title="Customer Tiers" testid="cx-tiers-panel">
          <Bars
            testid="cx-tier-bars"
            total={tiersTotal}
            colorFor={(k) => (TIER_META.find((t) => t.k === k) || {}).color}
            rows={TIER_META.map((t) => ({ key: t.k, label: t.k, value: (data.tiers || {})[t.k] || 0, note: t.note }))}
          />
        </Panel>
      </div>

      {/* ── Dormancy spectrum ──────────────────────────────── */}
      <Panel title="Dormancy Spectrum" testid="cx-spectrum-panel"
        actions={
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setWorklist({ bucket: 'at_risk', label: 'At Risk' })} data-testid="open-worklist-at_risk"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0A2540] bg-[#F4F0E8] hover:bg-[#EDE6D8] rounded-full px-3 py-1.5 transition-colors">
              <Phone size={13} /> Win-back · At Risk ({fmtNum((data.spectrum || {}).at_risk || 0)})
            </button>
            <button onClick={() => setWorklist({ bucket: 'lost', label: 'Lost' })} data-testid="open-worklist-lost"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#0A2540] hover:bg-[#0A2540]/90 rounded-full px-3 py-1.5 transition-colors">
              <Phone size={13} /> Win-back · Lost ({fmtNum((data.spectrum || {}).lost || 0)})
            </button>
          </div>
        }>
        <p className="text-xs text-[#A8A29E] mb-3">Days since last refill. Use the buttons above (or click <span className="font-semibold text-[#57534E]">At Risk</span> / <span className="font-semibold text-[#57534E]">Lost</span> below) to open a win-back call list.</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
          <div style={{ height: 200 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={spectrumDonut} dataKey="value" nameKey="name" innerRadius={54} outerRadius={86} paddingAngle={2}>
                  {spectrumDonut.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [fmtNum(v), n]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2.5">
            {SPECTRUM_META.map((s) => {
              const v = (data.spectrum || {})[s.k] || 0;
              const pct = Math.round((v / spectrumTotal) * 100);
              const clickable = (s.k === 'at_risk' || s.k === 'lost') && v > 0;
              return (
                <div key={s.k}
                  onClick={clickable ? () => setWorklist({ bucket: s.k, label: s.label }) : undefined}
                  role={clickable ? 'button' : undefined}
                  className={`flex items-center gap-3 text-sm rounded-lg -mx-1 px-1 py-0.5 ${clickable ? 'cursor-pointer hover:bg-[#FCFAF6]' : ''}`}
                  data-testid={`spectrum-${s.k}`}>
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.color }} />
                  <span className="text-[#44403C] w-28">{s.label}{clickable && <span className="text-[#C9A35B] ml-1">→</span>}</span>
                  <span className="text-[#A8A29E] text-xs w-14">{s.range}</span>
                  <div className="flex-1 h-2 rounded-full bg-[#F4F0E8] overflow-hidden"><div style={{ width: `${pct}%`, background: s.color }} className="h-full" /></div>
                  <span className="font-semibold text-[#0A2540] w-20 text-right">{fmtNum(v)} · {pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </Panel>

      {/* ── By category ────────────────────────────────────── */}
      <Panel title="Performance by Customer Category" testid="cx-category-panel">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.15em] uppercase text-[#78716C] border-b border-[#EDE6D8]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                <th className="py-2 font-semibold">Category</th>
                <th className="py-2 font-semibold text-right">Registered</th>
                <th className="py-2 font-semibold text-right">Active (mo)</th>
                <th className="py-2 font-semibold text-right">Active Rate</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_category || []).map((c) => (
                <tr key={c.category} className="border-b border-[#F4F0E8]" data-testid={`cat-row-${c.category}`}>
                  <td className="py-2.5 text-[#0A2540] font-medium">{c.category}</td>
                  <td className="py-2.5 text-right text-[#44403C]">{fmtNum(c.registered)}</td>
                  <td className="py-2.5 text-right text-[#44403C]">{fmtNum(c.active)}</td>
                  <td className="py-2.5 text-right"><span className="font-semibold text-[#0A2540]">{c.active_rate}%</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      {drillMonth && <AruDrillDialog month={drillMonth} entityId={entityId} onClose={() => setDrillMonth(null)} />}
      {worklist && <WorklistDialog bucket={worklist.bucket} label={worklist.label} entityId={entityId} onOpen={(sid) => { setWorklist(null); setOpenSub(sid); }} onClose={() => setWorklist(null)} />}
      {openSub && <Customer360 subId={openSub} onClose={() => setOpenSub(null)} />}
    </div>
  );
}
